import introJS from "intro.js";
import { ref } from "vue";
import { useRoute } from "vue-router";
import { defineStore } from "pinia";

import { update } from "../api/profile";
import { introJsButton } from "../utils/styles";
import { setAuthState } from "../composables/auth"

import { 
    interviewAiProcessOnboarding,
    interviewProcessOnboarding
} from "../utils/onboarding"

const ONBOARDING_FIELDS_MAP = {
    'interview-ai-interview': 'interview_ai_onboarding_passed',
    'interview-ai': 'interview_ai_dashboard_onboarding_passed'
}

const INTRO_JS_OPTIONS = {
    disableInteraction: true,
    showBullets: true,
    exitOnOverlayClick: false,
    exitOnEsc: false,
    prevLabel: 'Previous',
    nextLabel: 'Next',
    doneLabel: "Finish",
    tooltipClass: 'onboarding-tooltip',
    buttonClass: introJsButton,
}

const { setUser, getUser } = setAuthState()

export const useInterviewAiOnboardingStore = defineStore("interview-onboarding", () => {
    const route = useRoute();

    const dashboardFlowOnboardingInProgress = ref(false);
    const interviewFlowOnboardingInProgress = ref(false);

    const showOnboardingModal = ref(false);
    const domLoadDelay = (delay = 200) => new Promise((resolve, reject) => setTimeout(() => resolve(), delay));

    const onboardingExitHandler = () => {
        const onboardingFieldName = getOnboardingFieldNameFromCurrentRoute();

        if(getUser()?.options?.[onboardingFieldName] !== true){
            update({
                options: {
                    ...getUser()?.options,
                    [onboardingFieldName]: true
                }
            }).then(response => {
                setUser(response.data.data)
            })
        }
    }

    const getOnboardingFieldNameFromCurrentRoute = () => {
        return ONBOARDING_FIELDS_MAP[route.name]
    }

    const wasDashboardOnboardingPassed = () => {
        if(getUser()?.options?.[getOnboardingFieldNameFromCurrentRoute()] != true) {
            showOnboardingModal.value = true;
        }
    }

    const wasInterviewOnboardingPassed = () => {
        if(getUser()?.options?.[getOnboardingFieldNameFromCurrentRoute()] != true) {
            return true
        }

        return false
    }

    const closeOnboardingModal = (markOnboardingAsPassed = false) => {
        showOnboardingModal.value = false;
        dashboardFlowOnboardingInProgress.value = false;
        interviewFlowOnboardingInProgress.value = false;

        if( markOnboardingAsPassed ){
            onboardingExitHandler();
        }
    }

    const beforeChangeHandler = (e) => {
        e.scrollIntoView({
            block: "center",
            inline: "center",
        })
    }

    const getIntroJsInstance = (onExit, options) => {
        return introJS()
        .onexit(onExit)
        .onbeforechange(beforeChangeHandler)
        .setOptions(options)
    }
    
    const startDashboardOnboardingHandler = async () => {
        closeOnboardingModal();
        dashboardFlowOnboardingInProgress.value = true;
    
        await domLoadDelay();

        const introJsInstance = getIntroJsInstance(onboardingExitHandler, {
            ...INTRO_JS_OPTIONS,
            steps: interviewAiProcessOnboarding()
        })

        introJsInstance.start();
    }

    const startInterviewOnboardingHandler = async (onExitCallback = null) => {
        closeOnboardingModal()
        interviewFlowOnboardingInProgress.value = true;

        await domLoadDelay();

        const onExit = () => {
            onboardingExitHandler()

            if(onExitCallback != null) {
                onExitCallback()
            }
        }

        const introJsInstance = getIntroJsInstance(onExit, {
            ...INTRO_JS_OPTIONS,
            steps: interviewProcessOnboarding()
        })

        introJsInstance.start()
    }

    return {
        dashboardFlowOnboardingInProgress,
        interviewFlowOnboardingInProgress,
        showOnboardingModal,

        wasDashboardOnboardingPassed,
        wasInterviewOnboardingPassed,

        startDashboardOnboardingHandler,
        startInterviewOnboardingHandler,
        closeOnboardingModal
    };
});
